import React, { useEffect, useState } from "react"
import { Alert, Button, Card, Form } from "react-bootstrap"
import deepEqual from "deep-equal"

import { stringifyDate } from "../../../../shared_components/utils"
import { LocaleFormattedUser } from "../../../internationalization/LocaleFormatted"
import { updateObject } from "../../../../Loaders"
import ChangeLogTable, { ChangeLogButton } from "../ChangeLogTable"
import { ActiveSpan, handleError, UserDropdown, statusOptions, StatusDropdown } from "./utils"

export default function EntityInformation({
  title,
  intl,
  canEdit = false,
  getTextStr,
  entity,
  onUpdate,
  updateUrl,
  deleteBranchUrl,
  deleteAlert = {},
  extraFields = [],
  canDelete = false,
  sectionColor = "var(--lanzalake-grey-light-2)",
}) {
  const prefix = `${entity.name}: `
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const fields = [
    { id: "general.name", msg: "Name", value: entity.name, key: "name", type: "input" },
    {
      id: "general.description",
      msg: "Description",
      value: entity.description,
      key: "description",
      type: "textarea",
    },
    { id: "general.created.on", msg: "Created On", value: stringifyDate(entity.created_on) },
    {
      id: "general.created.by",
      msg: "Created By",
      value: <LocaleFormattedUser user={entity.created_by} />,
    },
    { id: "general.updated.on", msg: "Updated On", value: stringifyDate(entity.updated_on) },
    {
      id: "general.status",
      msg: "Status",
      value: <ActiveSpan active={entity.active} getTextStr={getTextStr} />,
      key: "active",
      type: "statusDropdown",
      defaultOpt: statusOptions.find((s) => s.value === entity.active),
      options: statusOptions,
    },
    ...extraFields,
  ]
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState({})
  const getDraftEntity = () =>
    Object.fromEntries(fields.filter((r) => r.key).map((r) => [r.key, entity[r.key]]))
  useEffect(() => {
    setDraft(getDraftEntity())
  }, [entity])
  const [showLog, setShowLog] = useState(false)
  const setValue = (key, val) => {
    setDraft((prev) => ({
      ...prev,
      [key]: val,
    }))
    error && setError("")
  }
  const deleteEntityBranch = () => {
    let msg = "This action is permanent and will result in the deletion of: "
    Object.entries(deleteAlert)
      .filter(([k, v]) => v.length > 0)
      .forEach(([k, v]) => {
        msg += `\n${v.length} ${k}: ${v.map((child) => `'${child.name}'`).join(", ")}`
      })
    msg += ". Do you wish to proceed?"
    if (!confirm(msg)) {
      return false
    }

    setLoading(true)
    updateObject(deleteBranchUrl, {}, true)
      .then((_) => {
        setLoading(false)
        onUpdate()
      })
      .catch((err) => {
        handleError(err, setError)
        setLoading(false)
      })
  }
  return (
    <Card className={"mb-1"} style={{ backgroundColor: sectionColor }}>
      <Card.Body style={{ padding: "1rem" }}>
        {error && <Alert variant={"danger"}>{error}</Alert>}
        <div style={{ display: "flex" }}>
          {title && (
            <h4 style={{ margin: "0" }}>
              <b>{title}</b>
            </h4>
          )}
          <div style={{ marginLeft: "auto", display: "flex" }}>
            <Button
              variant={"outline-primary"}
              size={"sm"}
              disabled={!canEdit}
              onClick={(e) => {
                e.preventDefault()
                setIsEditing(!isEditing)
                !deepEqual(getDraftEntity(), draft) && setDraft(getDraftEntity())
                error && setError("")
              }}
            >
              <span className={"fa fa-pencil-square-o"} />
              &nbsp;
              {isEditing ? getTextStr("general.reset", "Reset") : getTextStr("general.edit", "Edit")}
            </Button>
            {(entity?.change_logs || []).length > 0 && (
              <ChangeLogButton
                prefix={prefix}
                intl={intl}
                onClick={() => setShowLog(!showLog)}
                isExpanded={showLog}
              />
            )}
            {canDelete && (
              <>
                &nbsp;
                <Button
                  variant={"outline-primary"}
                  className={"btn btn-sm"}
                  disabled={!canDelete || !canEdit}
                  onClick={deleteEntityBranch}
                >
                  <span className={"fa fa-trash-o text-danger"} />
                </Button>
              </>
            )}
          </div>
        </div>
        {showLog && <ChangeLogTable intl={intl} changeLogs={entity?.change_logs || []} />}
        <hr />
        <Form
          className={"row mt-3"}
          onSubmit={(e) => {
            e.preventDefault()
            setLoading(true)
            updateObject(updateUrl, draft, true)
              .then((_) => {
                setIsEditing(false)
                setLoading(false)
                onUpdate()
              })
              .catch((err) => {
                handleError(err, setError)
                setLoading(false)
              })
          }}
        >
          {fields.map((f, idx) => {
            const { msg, id, value, type, key, defaultOpt, options } = f
            return (
              <div className={"col-md-6 mb-1"} key={`${msg}${idx}`}>
                <strong>{getTextStr(id, msg)}:</strong>
                &nbsp;
                {isEditing && type && key ? (
                  <>
                    {["textarea", "input"].includes(type) ? (
                      <Form.Control
                        as={type}
                        rows={2}
                        value={draft[key]}
                        onChange={(e) => setValue(key, e.target.value)}
                        required
                      />
                    ) : type === "userDropdown" ? (
                      <UserDropdown
                        field={key}
                        current={defaultOpt}
                        setValue={(field, value) => setValue(field, value)}
                        options={options}
                      />
                    ) : type === "statusDropdown" ? (
                      <StatusDropdown
                        showLabel={false}
                        options={options}
                        defaultValue={defaultOpt}
                        setValue={(val) => setValue(key, val)}
                      />
                    ) : (
                      <>{value || "-"}</>
                    )}
                  </>
                ) : (
                  <>{value || "-"}</>
                )}
              </div>
            )
          })}
          {isEditing && (
            <div className={"col-md-12 text-end"}>
              <Button
                type={"submit"}
                variant={"primary"}
                size={"sm"}
                disabled={!!(deepEqual(getDraftEntity(), draft) || loading || error)}
              >
                {loading ? getTextStr("general.loading", "Loading") : getTextStr("general.update", "Update")}
              </Button>
            </div>
          )}
        </Form>
      </Card.Body>
    </Card>
  )
}
